import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogContent, Dialog, Button, MobileStepper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { CR_EN_STEPS, CR_DE_STEPS, AP_EN_STEPS, AP_DE_STEPS } from '@/assets/images/intro/intro-utils';
import { CREATOR, CREATOR_EXTERNAL, APPROVER, APPROVER_JV, CREATOR_JV } from '@/helpers/constants';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import { IcoButtonSimple } from '../UI/IcoButton/IcoButton';
import styles from './DialogIntroSequence.module.scss';

const useStyles = makeStyles(() => ({
  content: {
    overflow: 'unset',
  },
  stepper: {
    background: 'none',
    justifyContent: 'center',
  },
  dialogPaper: {
    width: '50vw',
    position: 'relative',
    overflowY: 'unset',
    fontSize: 'calc(100vh / 40)',
    minWidth: '65vw',
    minHeight: '82vh',
    '@media (min-width: 1920px)': {
      padding: '1vh 1vw',
    },
    '@media (max-width: 1920px) and (min-width: 768px)': {
      minHeight: '89vh',
    },
    '@media (max-width: 768px) and (min-width: 640px)': {
      minHeight: '92vh',
    },

    '@media (max-width: 640px) and (min-width: 480px)': {
      minHeight: '96vh',
    },
    '@media (max-width: 480px) and (min-width: 384px)': {
      minWidth: '55vw',
      minHeight: '90vh',
    },
    '@media (max-width: 384px)': {
      minWidth: '50vw',
      minHeight: '94vh',
    },
  },
  button: {
    fontSize: 'calc(100vh / 40)',
    margin: '1vh 1vw',
  },
}));

type DialogIntroSequenceProps = {
  close: () => void;
};

const DialogIntroSequence: React.FC<DialogIntroSequenceProps> = (props) => {
  const { close } = props;
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const classes = useStyles();
  const authService = useAuth();
  const roles = authService.getUserRole(true);
  const hasCreatorRole = roles?.includes(CREATOR) || roles?.includes(CREATOR_EXTERNAL) || roles?.includes(CREATOR_JV);
  const hasApproverRole = roles?.includes(APPROVER) || roles?.includes(APPROVER_JV);
  const onlyCreators = hasCreatorRole && !hasApproverRole;
  const onlyApprover = hasApproverRole && !hasCreatorRole;
  const bothRoles = hasCreatorRole && hasApproverRole;

  const checkUserRole = () => {
    if (onlyCreators || (bothRoles && activeStep < 6)) {
      return 'CR';
    }

    if (onlyApprover || (bothRoles && activeStep > 5)) {
      return 'AP';
    }

    return '';
  };

  const checkStep = () => bothRoles && activeStep > 5 ? activeStep - 6 : activeStep;

  const imagesByRolLang = (lang) => lang === 'EN' ? CR_EN_STEPS : CR_DE_STEPS;
  const rolesSequence = [CREATOR, CREATOR_JV, APPROVER, APPROVER_JV]

  const imagesByRol = rolesSequence.map((role) => ({[role]: imagesByRolLang(lang)})).reduce((acc, curr) => ({ ...acc, ...curr }), {});

  const steps = rolesSequence.reduce((acc, role) => (
    authService.checkRole(role) ? { ...acc, ...imagesByRol[role] } : acc
  ), {});

  const maxSteps = Object.keys(steps).length;

  const handleNext = () => setActiveStep((prevActiveStep) => prevActiveStep + 1);

  const handleBack = () => setActiveStep((prevActiveStep) => prevActiveStep - 1);

  useEffect(() => {
    if (maxSteps === 0) {
      close();
    }
  }, [maxSteps, close]);

  const currentStep = steps?.[`${checkUserRole()}_${lang}_SP${checkStep() + 1}`];

  return (
    <Dialog open maxWidth="md" classes={{ paper: classes.dialogPaper }} aria-labelledby="form-dialog-intro-sequence">
      <DialogContent className={classes.content}>
        <div className={styles.DialogIntroSequence} data-testid="intro-tour-dialog">
          <div id="intro-tour-title" className={styles.Title}>
            {t('intro.title')}
          </div>
          <div className={styles.Gallery}>
            <div className={styles.ButtonBack}>
              <IcoButtonSimple
                id="btn-back-intro"
                disabled={activeStep === 0}
                icon="icon-pfeil-chevron-links"
                handleClick={handleBack}
              />
            </div>
            {currentStep?.type === 'image' && (
              <img src={currentStep.content} className={styles.Image} alt={`step-${checkStep() + 1}`} />
            )}
            {currentStep?.type === 'component' && (
              <div className={styles.Image}> <currentStep.content /> </div>
            )}
            <div className={styles.ButtonNext}>
              <IcoButtonSimple
                id="btn-next-intro"
                disabled={activeStep === maxSteps - 1}
                icon="icon-pfeil-chevron-rechts"
                handleClick={handleNext}
              />
            </div>
          </div>
          <MobileStepper
            className={classes.stepper}
            steps={maxSteps}
            position="static"
            variant="dots"
            activeStep={activeStep}
            backButton={undefined}
            nextButton={undefined}
          />
        </div>
        <div className={styles.ButtonExit}>
          <Button id="btn-skip-intro" data-testid="btn-skip-intro" color="primary" onClick={close} className={classes.button}>
            {t('intro.exit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DialogIntroSequence;
